import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { invokeToolAction } from '@/lib/tool-action';
import { ViewActionButton } from '@/components/views/atoms/ViewActionButton';
import { toSafeUiMessage } from '@/lib/dashboard-errors';
import {
  ToolActionConfirmationDialog,
  type ToolActionConfirmationContext,
} from '@/components/views/atoms/ToolActionConfirmationDialog';
import type { LightViewAction } from '@shared/types/views';
import type {
  ToolActionConfirmationEdit,
  ToolActionResponse,
} from '@shared/types/tool-actions';

const autoPrepareCache = new Map<string, Promise<ToolActionResponse>>();

const buildPreparingLabel = (label: string): string => {
  const normalized = label.replace(/^préparer\s+/i, '').trim();
  return normalized
    ? `Proposition de ${normalized} en cours...`
    : 'Proposition en cours...';
};

export const AppChatActionProposalCard = ({
  messageId,
  action,
  autoPrepare,
  onConfirmationContextChange,
}: {
  messageId: string;
  action: LightViewAction;
  autoPrepare: boolean;
  onConfirmationContextChange: (context: ToolActionConfirmationContext | null) => void;
}) => {
  const [isRunningAction, setIsRunningAction] = useState(false);
  const [serverActionLabel, setServerActionLabel] = useState<string | null>(
    autoPrepare ? buildPreparingLabel(action.label) : null
  );
  const [serverActionTitle, setServerActionTitle] = useState<string | null>(null);
  const [confirmationResponse, setConfirmationResponse] = useState<ToolActionResponse | null>(null);
  const attemptedAutoPrepareRef = useRef(false);

  const prepareAction = useCallback(async (fromAutoPrepare: boolean) => {
    if (action.type !== 'server_action' || isRunningAction) return;

    let preparedConfirmation = false;
    let preserveFeedback = false;
    const cacheKey = `${messageId}:${action.actionKey}:${action.sourceRunId}:${action.actionSpec?.goal ?? action.label}`;
    setIsRunningAction(true);
    setServerActionLabel(buildPreparingLabel(action.label));
    setServerActionTitle(null);

    try {
      let request = fromAutoPrepare ? autoPrepareCache.get(cacheKey) : undefined;
      if (!request) {
        request = invokeToolAction({
          actionKey: action.actionKey,
          sourceRunId: action.sourceRunId,
          sourceActionId: action.sourceActionId ?? null,
          sourceActionIndex: action.sourceActionIndex ?? null,
          sourceActionLabel: action.sourceActionLabel ?? action.label,
          actionSpec: action.actionSpec ?? null,
          outcomeSpec: action.outcomeSpec ?? null,
        });
        if (fromAutoPrepare) {
          autoPrepareCache.set(cacheKey, request);
        }
      }

      const response = await request;
      setServerActionTitle(toSafeUiMessage(response.message, 'Action impossible.'));
      if (response.status === 'needs_confirmation' && response.preview && response.confirmationId) {
        preparedConfirmation = true;
        setConfirmationResponse(response);
        setServerActionLabel('Proposition prête à valider');
      } else if (
        response.status === 'completed'
        || response.status === 'partially_completed'
        || response.status === 'already_done'
      ) {
        setServerActionLabel(action.successLabel ?? response.message);
      } else if (response.status === 'needs_connection') {
        preserveFeedback = true;
        setServerActionLabel('Connexion requise');
      } else if (response.status === 'missing_info') {
        preserveFeedback = true;
        setServerActionLabel('Info manquante');
      } else {
        preserveFeedback = true;
        setServerActionLabel('Action impossible');
      }
    } catch (error) {
      preserveFeedback = true;
      if (fromAutoPrepare) {
        autoPrepareCache.delete(cacheKey);
      }
      setServerActionLabel('Action impossible');
      setServerActionTitle(toSafeUiMessage(
        error instanceof Error ? error.message : null,
        'Action impossible.'
      ));
    } finally {
      setIsRunningAction(false);
      if (!preparedConfirmation && !preserveFeedback) {
        window.setTimeout(() => {
          setServerActionLabel(null);
          setServerActionTitle(null);
        }, 3200);
      }
    }
  }, [action, isRunningAction, messageId]);

  useEffect(() => {
    if (!autoPrepare || action.type !== 'server_action' || attemptedAutoPrepareRef.current) return;
    attemptedAutoPrepareRef.current = true;
    void prepareAction(true);
  }, [action.type, autoPrepare, prepareAction]);

  const confirmServerAction = async (
    candidateIds: string[],
    confirmationEdits: ToolActionConfirmationEdit[]
  ) => {
    if (action.type !== 'server_action' || !confirmationResponse?.confirmationId || isRunningAction) return;
    setIsRunningAction(true);
    setServerActionLabel('Synchronisation...');
    try {
      const response = await invokeToolAction({
        actionKey: action.actionKey,
        sourceRunId: action.sourceRunId,
        sourceActionId: action.sourceActionId ?? null,
        sourceActionIndex: action.sourceActionIndex ?? null,
        sourceActionLabel: action.sourceActionLabel ?? action.label,
        actionSpec: action.actionSpec ?? null,
        outcomeSpec: action.outcomeSpec ?? null,
        confirmationId: confirmationResponse.confirmationId,
        confirmedCandidateIds: candidateIds,
        confirmationEdits,
      });
      setServerActionTitle(toSafeUiMessage(response.message, 'Action impossible.'));
      setServerActionLabel(
        response.status === 'completed' || response.status === 'partially_completed'
          ? action.successLabel ?? response.message
          : 'Action impossible'
      );
      if (response.status === 'completed' || response.status === 'partially_completed') {
        setConfirmationResponse(null);
        onConfirmationContextChange(null);
      }
    } catch (error) {
      setServerActionLabel('Action impossible');
      setServerActionTitle(toSafeUiMessage(
        error instanceof Error ? error.message : null,
        'Action impossible.'
      ));
    } finally {
      setIsRunningAction(false);
      window.setTimeout(() => {
        setServerActionLabel(null);
        setServerActionTitle(null);
      }, 4200);
    }
  };

  if (action.type !== 'server_action') {
    return <ViewActionButton action={action} />;
  }
  const displayLabel = serverActionLabel ?? (
    autoPrepare ? buildPreparingLabel(action.label) : action.label
  );

  return (
    <div className="mt-3 rounded-lg border border-domino-cardBorder bg-domino-white px-3 py-3 shadow-sm">
      <span className="text-[11px] font-semibold uppercase text-domino-inkSoft">
        Proposition
      </span>
      <div className="mt-2 flex items-start gap-2">
        {isRunningAction && (
          <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-domino-coral" />
        )}
        <p className="text-sm font-semibold leading-5 text-domino-ink">
          {displayLabel}
        </p>
      </div>
      {serverActionTitle && !confirmationResponse && !isRunningAction && (
        <p role="status" className="mt-2 text-xs leading-5 text-domino-inkSoft">
          {serverActionTitle}
        </p>
      )}
      {!autoPrepare && !serverActionLabel && !confirmationResponse && !isRunningAction && (
        <Button
          size="sm"
          className="mt-3"
          onClick={() => void prepareAction(false)}
        >
          {action.label}
        </Button>
      )}
      {serverActionLabel === 'Action impossible' && (
        <Button
          size="sm"
          variant="secondary"
          className="mt-3"
          disabled={isRunningAction}
          onClick={() => void prepareAction(false)}
        >
          Réessayer
        </Button>
      )}
      <ToolActionConfirmationDialog
        response={confirmationResponse}
        executing={isRunningAction}
        onClose={() => {
          setConfirmationResponse(null);
          setServerActionLabel(null);
          setServerActionTitle(null);
          onConfirmationContextChange(null);
        }}
        onConfirm={confirmServerAction}
        onContextChange={onConfirmationContextChange}
      />
    </div>
  );
};
